import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import { ProformaInvoiceRow } from "@/features/invoices/proforma-invoices/proforma-invoice-row.type";

/**
 * 프로포마 목록의 "..." 메뉴에서 올릴 수 있는 송금 영수증.
 *
 * 프로포마는 누가 부티크에 보내느냐(paidBy)에 따라 송금 요청의 모양이 다르다.
 *  - AGENCY: 바이어 -> 에이전시(원화), 에이전시 -> 부티크(외화), 끝나면 차액 정산
 *  - BUYER : 바이어 -> 부티크 하나뿐(거래명세서 자동 발급 프로포마 등). 에이전시를 거치지 않으니
 *            에이전시 앞 송금·선송금·차액이 없다.
 * 예전에는 paidBy 를 보지 않고 AGENCY 쪽 세 항목만 띄워서, 직접 송금 프로포마에서는 무엇을 눌러도
 * "송금 요청을 찾을 수 없습니다" 만 떴다.
 */
export type ReceiptUploadMenuKey =
  | "TO_AGENCY"
  | "TO_BOUTIQUE"
  | "ADJUSTMENT"
  | "BUYER_TO_BOUTIQUE";

export interface ReceiptUploadMenuItem {
  key: ReceiptUploadMenuKey;
  name: string;
  isDisabled: boolean;
  /** 영수증을 붙일 송금 요청. 없으면 notFoundMessage 를 보여 준다. */
  payment?: LightPayment;
  notFoundMessage: string;
  /** 바이어 입금 전에 에이전시가 부티크로 먼저 보내는 경우 — 올리기 전에 확인을 받는다. */
  confirmsPreRemittance: boolean;
}

export type ReceiptUploadMenuRow = Pick<
  ProformaInvoiceRow,
  | "paidBy"
  | "payments"
  | "isRemittedToAgency"
  | "isRemittedToBoutique"
  | "isRemittedAdjustment"
>;

const NOT_FOUND = "해당 송금 요청을 찾을 수 없습니다. 관리자에게 문의하세요.";

function between(
  payments: LightPayment[],
  sender: LightPayment["sender"]["type"],
  receiver: LightPayment["receiver"]["type"]
) {
  return payments.filter(
    (payment) =>
      payment.sender.type === sender &&
      // 부티크 자리는 3자무역사도 포함한다(3자무역이면 3자무역사가 받는다)
      (receiver === "BOUTIQUE"
        ? isBoutiqueSide(payment.receiver.type)
        : payment.receiver.type === receiver)
  );
}

export function getReceiptUploadMenu(
  row: ReceiptUploadMenuRow
): ReceiptUploadMenuItem[] {
  const payments = row.payments ?? [];

  if (row.paidBy === "BUYER") {
    const candidates = between(payments, "BUYER", "BOUTIQUE");
    return [
      {
        key: "BUYER_TO_BOUTIQUE",
        name: "송금 영수증 업로드(Buyer → Boutique)",
        isDisabled: row.isRemittedToBoutique,
        // 나눠 보낸 경우 아직 덜 보낸 송금 요청에 붙인다
        payment:
          candidates.find((payment) => !payment.isRemitted) ?? candidates[0],
        notFoundMessage:
          "바이어 → 부티크 송금 요청이 없습니다. 송금 요청을 먼저 등록해 주세요.",
        confirmsPreRemittance: false,
      },
    ];
  }

  const toBoutique = between(payments, "AGENCY", "BOUTIQUE")[0];
  return [
    {
      key: "TO_AGENCY",
      name: "송금 영수증 업로드(To Agency)",
      isDisabled: row.isRemittedToAgency,
      payment: between(payments, "BUYER", "AGENCY")[0],
      notFoundMessage: NOT_FOUND,
      confirmsPreRemittance: false,
    },
    {
      key: "TO_BOUTIQUE",
      name: "송금 영수증 업로드(To Boutique)",
      isDisabled: row.isRemittedToBoutique,
      payment: toBoutique,
      notFoundMessage: NOT_FOUND,
      confirmsPreRemittance: !row.isRemittedToAgency,
    },
    {
      key: "ADJUSTMENT",
      name: "차액 송금 영수증 업로드",
      isDisabled: !(
        row.isRemittedToAgency &&
        row.isRemittedToBoutique &&
        !row.isRemittedAdjustment
      ),
      // 돌려줄 차액(에이전시 -> 바이어)이 없으면 더 받을 차액(바이어 -> 에이전시, 가장 나중 것)
      payment:
        between(payments, "AGENCY", "BUYER")[0] ??
        between(payments, "BUYER", "AGENCY").slice(-1)[0],
      notFoundMessage:
        "차액 송금 요청을 찾을 수 없습니다. 관리자에게 문의하세요.",
      confirmsPreRemittance: false,
    },
  ];
}

export const RECEIPT_UPLOAD_MENU_KEYS: ReceiptUploadMenuKey[] = [
  "TO_AGENCY",
  "TO_BOUTIQUE",
  "ADJUSTMENT",
  "BUYER_TO_BOUTIQUE",
];
